import { ReactElement } from 'react';
import { useEntity } from 'components';
import { activityTypes } from 'types';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  LoadingContainer,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@ff-it/ui';
import useSWR, { KeyedMutator } from 'swr';
import { PlanningConfig as PlanningConfigType } from 'modules/campaign/block/types';
import { ActivityIcon, BoolIcon } from 'components/Icon';
import { DialogForm } from './DialogForm';

type ConfigTableProps = { planning: PlanningConfigType; endpoint: string; mutate: KeyedMutator<any> };

function ConfigTable({ planning, endpoint, mutate }: ConfigTableProps): ReactElement {
  return (
    <Table size="sm" mb="3">
      <colgroup>
        <col width="45px" />
        <col />
        <col />
        <col />
        <col />
        <col width="45px" />
        <col width="45px" />
      </colgroup>
      <Thead>
        <Tr>
          <Th />
          <Th>Type</Th>
          <Th>Status template</Th>
          <Th>Default planning strategy</Th>
          <Th>Asset delivery contact</Th>
          <Th lineHeight="1" fontSize="xxs" fontWeight="regular">
            Asset delivery deadline
          </Th>
          <Th />
        </Tr>
      </Thead>
      <Tbody>
        {activityTypes.map((type) => {
          const {
            enabled,
            block_status_template,
            default_plan_strategy,
            lock,
            asset_delivery_contact,
            asset_delivery_deadline,
          } = planning[type];
          return (
            <Tr key={type}>
              <Td>
                <BoolIcon>{enabled}</BoolIcon>
              </Td>
              <Td>
                <ActivityIcon type={type} className="mr-2" />
                {type}
              </Td>
              <Td>
                {block_status_template?.name || (
                  <Box as="span" color="disabled">
                    unspecified
                  </Box>
                )}
              </Td>
              <Td>
                <Icon icon={lock ? 'lock' : 'lock-open'} className="mr-2" />
                {default_plan_strategy}
              </Td>
              <Td>{asset_delivery_contact}</Td>
              <Td textAlign="center">{asset_delivery_deadline}</Td>
              <Td textAlign="end">
                <Dialog canDismiss={false}>
                  <DialogTrigger>
                    <Button icon="pencil" size="xs" variant="secondary" testId={`config-${type}`} />
                  </DialogTrigger>
                  <DialogContent testId="edit-plan-config">
                    <DialogForm endpoint={endpoint} type={type} initialValues={planning[type]} mutate={mutate} />
                  </DialogContent>
                </Dialog>
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}

export function PlanningConfig(): ReactElement {
  const { endpoint } = useEntity();
  const { data, error, isLoading, mutate } = useSWR<{ planning: PlanningConfigType }>(
    { url: `${endpoint}planning/` },
    { keepPreviousData: false },
  );

  if (error) {
    throw error;
  }

  return (
    <div className="container ml-0">
      <LoadingContainer loading={isLoading} className="flex-grow-1 flex-column d-flex">
        {data && <ConfigTable endpoint={endpoint} mutate={mutate} {...data} />}
      </LoadingContainer>
    </div>
  );
}
